// htmlJsLibraries — the approved-library registry for freeform "htmljs" report components (M5).
//
// A sandboxed htmljs component has no network of its own (CSP connect-src 'none'), so it cannot pull
// a library from a CDN at runtime. The supported way to use a library is therefore "host-injected":
// the admin declares `libraries: [<id>, ...]` on the component, and the host inlines each approved
// bundle into the srcDoc <head> (before the author code) as a trusted <script>. Because the bundle
// is part of the document, script-src 'unsafe-inline' already covers it — no network, no CDN, one
// vetted copy shared by every component. Declaring a library is admin-gated server-side (it changes
// what runs), exactly like the html body — see usalco_api/src/lib/v4.php (v4_htmlJsBodyHash).
//
// The server keeps a matching id allow-list (v4_htmlJsLibraryIds() in v4.php) and rejects any id not
// on it; this registry holds the id -> source so the client can actually inject it. KEEP THE TWO ID
// LISTS IN SYNC.
//
// Heavy third-party bundles (e.g. Syncfusion) are a future registry entry: because inlining several
// MB into every document is impractical, those are the case the plan serves from a dedicated
// cookieless component origin with script-src '<that origin>' + Subresource Integrity (an infra
// step). The declaration mechanism and the admin gate are identical either way. See
// ai/requests/freeform_report/ (M5 / section 10).

// ReservoirKit — a small, dependency-free helper the component code can rely on without pulling any
// external library: escape/format utilities, a safe markdown renderer (the AI analysis channel from
// M4 returns markdown in ai.finalText), a quick table renderer over the v4 data shape, and a
// one-call renderer for an analyze() result. It is authored here AS THE INJECTED SOURCE STRING so
// there is a single source of truth: the same bytes are injected into the iframe and exercised by
// htmlJsLibraries.test.js (which evaluates this string). The IIFE assigns window.ReservoirKit and
// also returns it (so a test can capture it by evaluating the expression).
export const RESERVOIR_KIT_SOURCE = `(function(root){
	function escapeHtml(s){
		return String(s==null?'':s)
			.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
			.replace(/"/g,'&quot;').replace(/'/g,'&#39;');
	}
	function safeUrl(u){
		u=String(u==null?'':u).trim();
		return /^(https?:|mailto:)/i.test(u)?u:'';
	}
	function formatNumber(v,opts){
		if(v==null||v==='')return '';
		var n=typeof v==='number'?v:Number(v);
		if(!isFinite(n))return escapeHtml(v);
		try{return new Intl.NumberFormat(undefined,opts||{}).format(n);}catch(e){return String(n);}
	}
	// Inline markdown on text that is ALREADY html-escaped, so author '<' is already '&lt;' and the
	// only tags in the output are the ones we insert.
	function inline(t){
		// inline code first so its contents are not touched by the emphasis passes
		t=t.replace(/\`([^\`]+)\`/g,function(_,c){return '<code>'+c+'</code>';});
		// links [text](url) — text already escaped; url validated + quote-safe
		t=t.replace(/\\[([^\\]]+)\\]\\(([^)\\s]+)\\)/g,function(m,txt,url){
			var u=safeUrl(url);
			if(!u)return txt;
			return '<a href="'+u.replace(/"/g,'%22')+'" target="_blank" rel="noopener noreferrer">'+txt+'</a>';
		});
		t=t.replace(/\\*\\*([^*]+)\\*\\*/g,'<strong>$1</strong>');
		t=t.replace(/__([^_]+)__/g,'<strong>$1</strong>');
		t=t.replace(/(^|[^*])\\*([^*\\s][^*]*?)\\*/g,'$1<em>$2</em>');
		t=t.replace(/(^|[^_])_([^_\\s][^_]*?)_/g,'$1<em>$2</em>');
		return t;
	}
	function renderMarkdown(md){
		var lines=String(md==null?'':md).replace(/\\r\\n/g,'\\n').split('\\n');
		var out=[],i=0,listType=null;
		function closeList(){ if(listType){out.push('</'+listType+'>');listType=null;} }
		while(i<lines.length){
			var line=lines[i];
			// fenced code block
			var fence=line.match(/^\\s*\`\`\`(.*)$/);
			if(fence){
				closeList();
				var buf=[];i++;
				while(i<lines.length && !/^\\s*\`\`\`\\s*$/.test(lines[i])){buf.push(lines[i]);i++;}
				i++; // skip closing fence
				out.push('<pre><code>'+escapeHtml(buf.join('\\n'))+'</code></pre>');
				continue;
			}
			if(/^\\s*$/.test(line)){closeList();i++;continue;}
			var hr=/^\\s*(?:---|\\*\\*\\*|___)\\s*$/.test(line);
			if(hr){closeList();out.push('<hr>');i++;continue;}
			var h=line.match(/^(#{1,6})\\s+(.*)$/);
			if(h){closeList();var lvl=h[1].length;out.push('<h'+lvl+'>'+inline(escapeHtml(h[2].trim()))+'</h'+lvl+'>');i++;continue;}
			var bq=line.match(/^\\s*>\\s?(.*)$/);
			if(bq){closeList();out.push('<blockquote>'+inline(escapeHtml(bq[1]))+'</blockquote>');i++;continue;}
			var ol=line.match(/^\\s*\\d+\\.\\s+(.*)$/);
			var ul=line.match(/^\\s*[-*+]\\s+(.*)$/);
			if(ol||ul){
				var want=ol?'ol':'ul';
				if(listType!==want){closeList();out.push('<'+want+'>');listType=want;}
				out.push('<li>'+inline(escapeHtml((ol?ol[1]:ul[1])))+'</li>');
				i++;continue;
			}
			// paragraph: gather consecutive non-blank, non-special lines
			closeList();
			var para=[line];i++;
			while(i<lines.length && !/^\\s*$/.test(lines[i])
				&& !/^(#{1,6})\\s/.test(lines[i]) && !/^\\s*>/.test(lines[i])
				&& !/^\\s*\`\`\`/.test(lines[i]) && !/^\\s*[-*+]\\s/.test(lines[i])
				&& !/^\\s*\\d+\\.\\s/.test(lines[i])){para.push(lines[i]);i++;}
			out.push('<p>'+inline(escapeHtml(para.join('\\n'))).replace(/\\n/g,'<br>')+'</p>');
		}
		closeList();
		return out.join('');
	}
	// Render a v4 data table (fields:[{name,displayName?}], rows:[[...]]) into el (or return HTML).
	function renderTable(fields,rows,el){
		fields=Array.isArray(fields)?fields:[];
		rows=Array.isArray(rows)?rows:[];
		var head='<thead><tr>'+fields.map(function(f){
			return '<th>'+escapeHtml(f&&(f.displayName||f.name)||'')+'</th>';
		}).join('')+'</tr></thead>';
		var body='<tbody>'+rows.map(function(r){
			return '<tr>'+fields.map(function(_,c){return '<td>'+escapeHtml((r||[])[c])+'</td>';}).join('')+'</tr>';
		}).join('')+'</tbody>';
		var html='<table class="rk-table">'+head+body+'</table>';
		if(el&&el.innerHTML!=null){el.innerHTML=html;return el;}
		return html;
	}
	// Render an analyze() result { finalText, displayParts:[{toolName,result}] }.
	function renderAnalysis(ai,el){
		var parts=[];
		if(ai&&ai.finalText)parts.push('<div class="rk-md">'+renderMarkdown(ai.finalText)+'</div>');
		var dp=ai&&Array.isArray(ai.displayParts)?ai.displayParts:[];
		dp.forEach(function(p){
			if(!p)return;
			parts.push('<div class="rk-part" data-tool="'+escapeHtml(p.toolName)+'"><pre>'+
				escapeHtml(typeof p.result==='string'?p.result:JSON.stringify(p.result,null,2))+'</pre></div>');
		});
		var html=parts.join('');
		if(el&&el.innerHTML!=null){el.innerHTML=html;return el;}
		return html;
	}
	var K={escapeHtml:escapeHtml,safeUrl:safeUrl,formatNumber:formatNumber,
		renderMarkdown:renderMarkdown,renderTable:renderTable,renderAnalysis:renderAnalysis};
	if(root)root.ReservoirKit=K;
	return K;
})(typeof window!=='undefined'?window:this);`;

// The registry: id -> { id, name, global, source }. `global` is the window property the bundle
// exposes inside the sandbox (shown in the builder so authors know what to call). Add new approved
// libraries here AND to v4_htmlJsLibraryIds() in usalco_api/src/lib/v4.php.
export const HTMLJS_LIBRARIES = {
	'reservoir-kit': {
		id: 'reservoir-kit',
		name: 'Reservoir Kit',
		global: 'ReservoirKit',
		source: RESERVOIR_KIT_SOURCE,
	},
};

export const HTMLJS_LIBRARY_IDS = Object.keys(HTMLJS_LIBRARIES);

// Build the <script> tags for the requested, allow-listed library ids, in a stable order. Unknown
// ids are dropped (the server already rejected them on save; this is defense in depth). The source
// is injected inline, so it is covered by script-src 'unsafe-inline' and needs no network.
export function buildLibraryScripts(libraryIds) {
	if (!Array.isArray(libraryIds)) return '';
	const seen = new Set();
	const out = [];
	for (const id of libraryIds) {
		if (typeof id !== 'string' || seen.has(id)) continue;
		seen.add(id);
		const lib = HTMLJS_LIBRARIES[id];
		if (!lib) continue;
		out.push('<script>' + lib.source + '</script>');
	}
	return out.join('');
}
